/**
 * SPEC §6.3 — explicit-render Turnstile, `interaction-only`, one token per
 * submission. If the script does not load, the caller hides the contribute
 * button and keeps the map: never a broken form.
 */

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  execute(id: string): void;
  reset(id: string): void;
  remove(id: string): void;
}
declare global { interface Window { turnstile?: TurnstileApi } }

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export class Turnstile {
  private api: TurnstileApi | null = null;
  private container: HTMLElement;
  constructor(private siteKey: string, parent: HTMLElement) {
    this.container = document.createElement('div');
    this.container.className = 'turnstile';
    parent.append(this.container);
  }

  /** Resolves false if the widget cannot load; the button should then be hidden. */
  async load(timeoutMs = 8000): Promise<boolean> {
    if (window.turnstile) { this.api = window.turnstile; return true; }
    return new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = SCRIPT;
      s.async = true;
      const timer = setTimeout(() => resolve(false), timeoutMs);
      s.onload = () => { clearTimeout(timer); this.api = window.turnstile ?? null; resolve(!!this.api); };
      s.onerror = () => { clearTimeout(timer); resolve(false); };
      document.head.append(s);
    });
  }

  /** Mint one token. A fresh widget per call keeps tokens single-use by construction. */
  token(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.api) return reject(new Error('turnstile_unavailable'));
      const api = this.api;
      let id = '';
      const finish = (fn: () => void) => { fn(); try { api.remove(id); } catch { /* already gone */ } };
      id = api.render(this.container, {
        sitekey: this.siteKey,
        appearance: 'interaction-only',
        execution: 'execute',
        callback: (t: string) => finish(() => resolve(t)),
        'error-callback': () => finish(() => reject(new Error('turnstile_failed'))),
        'timeout-callback': () => finish(() => reject(new Error('turnstile_timeout'))),
      });
      api.execute(id);
    });
  }
}
